import React from 'react'

/**
 * Inline SVG icon set.
 *
 * These replace the emoji that previously stood in for product iconography.
 * Emoji render differently per-OS, can't inherit text color or weight, and
 * read as placeholder art in a financial UI. Every glyph here is drawn on a
 * 20x20 grid with a 1.5px stroke and `currentColor`, so an icon always picks
 * up the color and optical weight of the text it sits next to.
 *
 * Add a glyph by adding one entry to `PATHS` — nothing else needs to change.
 */

export type IconName =
  | 'vault'
  | 'clipboard'
  | 'waveform'
  | 'sparkle'
  | 'target'
  | 'layers'
  | 'gauge'
  | 'lock'
  | 'shield'
  | 'card'
  | 'trending'
  | 'receipt'
  | 'wallet'
  | 'check'
  | 'alert'
  | 'paperclip'
  | 'send'
  | 'mic'
  | 'close'
  | 'copy'
  | 'menu'
  | 'plus'

const PATHS: Record<IconName, React.ReactNode> = {
  // Accounts — a vault door, not a generic bank building.
  vault: (
    <>
      <rect x="2.5" y="3.5" width="15" height="13" rx="2" />
      <circle cx="10" cy="10" r="3.25" />
      <path d="M10 5.5v1.25M10 13.25v1.25M14.5 10h-1.25M6.75 10H5.5" />
    </>
  ),
  clipboard: (
    <>
      <path d="M7.5 3.5h5a1 1 0 0 1 1 1v.5h1.5a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1h-11a1 1 0 0 1-1-1v-10a1 1 0 0 1 1-1H6.5v-.5a1 1 0 0 1 1-1Z" />
      <path d="M7 9.5h6M7 12.5h4" />
    </>
  ),
  // Voice — a waveform reads as "live audio" better than a microphone does.
  waveform: (
    <>
      <path d="M3 8.5v3M6.5 5.5v9M10 3.5v13M13.5 6.5v7M17 9v2" />
    </>
  ),
  sparkle: (
    <>
      <path d="M10 2.5 11.6 7.2a1.5 1.5 0 0 0 1.2 1L17.5 10l-4.7 1.8a1.5 1.5 0 0 0-1.2 1L10 17.5l-1.6-4.7a1.5 1.5 0 0 0-1.2-1L2.5 10l4.7-1.8a1.5 1.5 0 0 0 1.2-1Z" />
    </>
  ),
  target: (
    <>
      <circle cx="10" cy="10" r="7" />
      <circle cx="10" cy="10" r="3.25" />
      <circle cx="10" cy="10" r="0.6" fill="currentColor" stroke="none" />
    </>
  ),
  layers: (
    <>
      <path d="M10 2.75 17.25 7 10 11.25 2.75 7Z" />
      <path d="m3.5 10.5 6.5 3.8 6.5-3.8" />
      <path d="m3.5 13.75 6.5 3.8 6.5-3.8" />
    </>
  ),
  gauge: (
    <>
      <path d="M3 14.5a7.5 7.5 0 1 1 14 0" />
      <path d="M10 14.5 13 8.5" />
      <circle cx="10" cy="14.5" r="1.1" fill="currentColor" stroke="none" />
    </>
  ),
  lock: (
    <>
      <rect x="3.75" y="8.5" width="12.5" height="8.5" rx="1.75" />
      <path d="M6.75 8.5V6.25a3.25 3.25 0 0 1 6.5 0V8.5" />
    </>
  ),
  shield: (
    <>
      <path d="M10 2.75 16 5v5.1c0 3.4-2.4 6.3-6 7.15-3.6-.85-6-3.75-6-7.15V5Z" />
      <path d="m7.5 9.75 1.9 1.9 3.4-3.6" />
    </>
  ),
  card: (
    <>
      <rect x="2.5" y="4.75" width="15" height="10.5" rx="2" />
      <path d="M2.5 8.5h15M5.5 12.25h3" />
    </>
  ),
  trending: (
    <>
      <path d="m3 13.5 4.5-4.5 3 3 6.5-6.5" />
      <path d="M12.75 5.5h4.25v4.25" />
    </>
  ),
  receipt: (
    <>
      <path d="M4.5 2.75h11v14.5l-2.2-1.4-2.15 1.4-2.15-1.4-2.15 1.4-2.35-1.4Z" />
      <path d="M7.5 7h5M7.5 10.25h3.5" />
    </>
  ),
  wallet: (
    <>
      <path d="M3 6.5a2 2 0 0 1 2-2h9.5a1.5 1.5 0 0 1 1.5 1.5v1" />
      <rect x="3" y="6.5" width="14" height="9.5" rx="2" />
      <circle cx="13.25" cy="11.25" r="1.1" fill="currentColor" stroke="none" />
    </>
  ),
  check: <path d="m4.5 10.5 3.6 3.5 7.4-8" />,
  alert: (
    <>
      <path d="M10 3.4 18 16.6H2Z" />
      <path d="M10 8v3.25" />
      <circle cx="10" cy="13.9" r="0.75" fill="currentColor" stroke="none" />
    </>
  ),
  paperclip: (
    <path d="M14.5 9.25 9.4 14.4a3.25 3.25 0 0 1-4.6-4.6l5.6-5.6a2.17 2.17 0 0 1 3.07 3.07l-5.6 5.6a1.08 1.08 0 0 1-1.54-1.54l4.95-4.95" />
  ),
  send: <path d="M17 3.5 9 11.5M17 3.5l-5.25 14-2.75-6-6-2.75Z" />,
  mic: (
    <>
      <rect x="7.5" y="2.5" width="5" height="9.5" rx="2.5" />
      <path d="M4.75 9.5a5.25 5.25 0 0 0 10.5 0M10 14.75v2.75" />
    </>
  ),
  close: <path d="m5 5 10 10M15 5 5 15" />,
  menu: <path d="M3 6h14M3 10h14M3 14h14" />,
  plus: <path d="M10 4.5v11M4.5 10h11" />,
  // Two offset sheets — the standard copy affordance.
  copy: (
    <>
      <rect x="7" y="7" width="9.5" height="9.5" rx="2" />
      <path d="M13 4.5H5.5a2 2 0 0 0-2 2V13" />
    </>
  ),
}

export interface IconProps extends React.SVGProps<SVGSVGElement> {
  name: IconName
  /** Rendered size in px. Defaults to 1em so it tracks surrounding type. */
  size?: number | string
  /** Accessible label. Omit for purely decorative icons (the default). */
  label?: string
}

/**
 * Render one glyph from the set.
 *
 * Decorative by default (`aria-hidden`), since most icons here sit beside a
 * text label that already carries the meaning. Pass `label` when the icon is
 * the only thing conveying it.
 */
export const Icon: React.FC<IconProps> = ({ name, size, label, ...rest }) => {
  const path = PATHS[name]
  if (!path) return null

  return (
    <svg
      viewBox="0 0 20 20"
      width={size ?? '1em'}
      height={size ?? '1em'}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
      {...rest}
    >
      {path}
    </svg>
  )
}

export default Icon
